import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute, ParamMap, Router } from '@angular/router';
import { CalcConfigService } from '_services/calc-config.service';
import { Formula } from '../../_models/formula';
import { FormulaComponent } from '../formula/formula.component';
import { combineLatest, Subscription } from 'rxjs';
import { filter as lodashFilter } from 'lodash-es';
import { MatButton } from '@angular/material/button';
import { MatIcon } from '@angular/material/icon';

import { AidYearComponent } from '../../_shared/aid-year/aid-year.component';

@Component({
    selector: 'app-formula-detail',
    templateUrl: './formula-detail.component.html',
    styleUrls: ['./formula-detail.component.css'],
    imports: [MatButton, MatIcon, AidYearComponent, FormulaComponent]
})
export class FormulaDetailComponent implements OnInit, OnDestroy {
  @ViewChild(FormulaComponent) private formulaComponent?: FormulaComponent;
  formula: Formula | null = null;
  formulaCopy: Formula | null = null;
  editMode = false;
  isNew = false;
  notFound = false;
  loading = true;

  private subscription?: Subscription;
  private schoolId = '';

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    public cc: CalcConfigService
  ) { }

  ngOnInit(): void {
    this.schoolId = this.route.parent?.parent?.snapshot.paramMap.get('schoolId') ?? '';
    this.subscription = combineLatest([this.route.paramMap, this.cc.formulas]).subscribe(([params, formulas]) => {
      this.resolveFormula(params, formulas);
    });
  }

  ngOnDestroy(): void {
    this.subscription?.unsubscribe();
  }

  startEdit(): void {
    if (!this.formula) {
      return;
    }
    this.formulaCopy = this.cloneFormula(this.formula);
    this.editMode = true;
  }

  save(): void {
    if (!this.formulaCopy) {
      return;
    }
    if (!this.formulaCopy.valid) {
      alert('Formula must be valid in order to save. Please fix or cancel.');
      this.formulaComponent?.focusFirstMathOperator();
      return;
    }
    const toSave = this.cloneFormula(this.formulaCopy);
    this.cc.saveFormula(this.schoolId, toSave);
    this.formula = toSave;
    this.formulaCopy = this.cloneFormula(toSave);
    this.editMode = false;
    if (this.isNew) {
      this.router.navigate(['../', toSave.formulaId], { relativeTo: this.route });
      this.isNew = false;
    }
  }

  cancel(): void {
    if (this.isNew) {
      this.router.navigate(['../'], { relativeTo: this.route });
      return;
    }
    if (this.formula) {
      this.formulaCopy = this.cloneFormula(this.formula);
    }
    this.editMode = false;
  }

  confirmDelete(): void {
    if (!this.formula) {
      return;
    }

    const usage = lodashFilter(this.cc.usedBy.value, { entityId: this.formula.formulaId, entityType: 'Formula' });
    if (usage.length > 0) {
      alert('This formula is being used. You need to remove uses before deleting the formula');
      return;
    }

    const confirmed = confirm(`Delete "${this.formula.name}"?`);
    if (!confirmed) {
      return;
    }

    this.cc.deleteFormula(this.schoolId, this.formula);
    this.router.navigate(['../'], { relativeTo: this.route });
  }

  goBack(): void {
    this.router.navigate(['../'], { relativeTo: this.route });
  }

  get activeFormulaName(): string {
    if (this.editMode) {
      return this.formulaCopy?.name || 'New Formula';
    }
    return this.formula?.name || 'New Formula';
  }

  private resolveFormula(params: ParamMap, formulas: Formula[]): void {
    const formulaId = params.get('formulaId');
    if (!formulaId) {
      if (!this.isNew) {
        this.initializeNewFormula();
      }
      this.loading = false;
      this.notFound = false;
      return;
    }

    this.isNew = false;
    const formula = formulas.find(f => f.formulaId === formulaId);
    if (!formula) {
      this.formula = null;
      this.formulaCopy = null;
      this.notFound = true;
      this.loading = false;
      return;
    }

    this.notFound = false;
    this.formula = formula;
    if (!this.editMode) {
      this.formulaCopy = this.cloneFormula(formula);
    }
    this.loading = false;
  }

  private initializeNewFormula(): void {
    const newFormula: Formula = {
      formulaId: this.cc.getUniqueId(),
      name: 'New formula',
      description: '',
      formula: '',
      valid: false
    };

    this.formula = newFormula;
    this.formulaCopy = this.cloneFormula(newFormula);
    this.isNew = true;
    this.editMode = true;
  }

  private cloneFormula(formula: Formula): Formula {
    return JSON.parse(JSON.stringify(formula));
  }
}
